import { useEffect, useRef, useState } from "react";
import { useFieldArray, type Control, type FieldValues } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { selectDocumentUpload } from "@shared-kernel/application/use-cases/document-upload/selectors/document-upload-selectors";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { Plus, Trash2, FileText, Loader2 } from "lucide-react";
import { uploadDocument, resetUploadDocument } from "@shared-kernel/application/use-cases/document-upload/upload-document";
import { STUDENT_DOCUMENT_MAX_FILE_SIZE, STUDENT_DOCUMENT_FILETYPE } from "@shared-kernel/application/ports/student/student-repository";
import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { PdfPreviewHoverCard } from "@shared-kernel/primary/shared/pdf-preview-hover-card/pdf-preview-hover-card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared-kernel/primary/shared/shadcn/ui/select";
import { FormControl, FormField, FormItem, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import {
  ATTACHMENT_LABEL_OPTIONS,
  type Attachment,
} from "@academy-context/shared/external-provider-batch/types/provider-training-form.types";

// Type for forms that have an attachments field
type FormWithAttachments = FieldValues & { attachments: Attachment[] };

interface DocumentUploadListProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  name: "attachments";
}

type AttachmentFieldItem = Attachment & { id: string };

export const DocumentUploadList = ({ control, name }: DocumentUploadListProps) => {
  const dispatch = useAppDispatch();
  const { url: uploadedUrl, processing } = useAppSelector(selectDocumentUpload);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { fields, append, remove, update } = useFieldArray<FormWithAttachments, "attachments">({
    control,
    name,
  });

  // Cast fields to include attachment properties
  const typedFields = fields as AttachmentFieldItem[];

  const [activeUploadIndex, setActiveUploadIndex] = useState<Nullable<number>>(null);
  const [error, setError] = useState<Nullable<string>>(null);

  // Handle successful upload
  useEffect(() => {
    if (processing === "success" && uploadedUrl && activeUploadIndex !== null) {
      const currentField = typedFields[activeUploadIndex];
      if (currentField) {
        update(activeUploadIndex, { url: uploadedUrl, label: currentField.label });
      }
      setActiveUploadIndex(null);
      dispatch(resetUploadDocument());
    }
  }, [processing, uploadedUrl, activeUploadIndex, typedFields, update, dispatch]);

  const handleAddClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset input for reselection
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Size validation
    if (file.size > STUDENT_DOCUMENT_MAX_FILE_SIZE) {
      setError(`La taille du fichier dépasse la limite de ${Math.round(STUDENT_DOCUMENT_MAX_FILE_SIZE / 1024 / 1024)} Mo`);
      return;
    }

    setError(null);

    // Add new field and start upload
    const newIndex = typedFields.length;
    append({ url: "", label: "" });
    setActiveUploadIndex(newIndex);
    dispatch(uploadDocument({ document: file, asAdmin: false, role: ROLES.STUDENT }));
  };

  const handleDelete = (index: number) => {
    // If this slot is currently uploading, cancel it
    if (activeUploadIndex === index) {
      setActiveUploadIndex(null);
      dispatch(resetUploadDocument());
    }

    // Adjust activeUploadIndex if deleting a slot before it
    if (activeUploadIndex !== null && index < activeUploadIndex) {
      setActiveUploadIndex(activeUploadIndex - 1);
    }

    remove(index);
  };

  const isUploading = processing === "pending";

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <input type="file" ref={fileInputRef} accept={STUDENT_DOCUMENT_FILETYPE.join(",")} onChange={handleFileSelect} className="hidden" />
        {typedFields.map((field, index) => {
          const isThisSlotUploading = activeUploadIndex === index && isUploading;

          return (
            <FormField
              key={field.id}
              control={control}
              name={`attachments.${index}.label`}
              render={({ field: labelField }) => (
                <FormItem className="space-y-1">
                  <div className="flex items-center gap-2 rounded-lg border p-3">
                    <div className="shrink-0">
                      {isThisSlotUploading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="size-4 animate-spin" />
                          <span>Téléchargement en cours...</span>
                        </div>
                      ) : field.url ? (
                        <PdfPreviewHoverCard documentUrl={field.url} triggerLabel="Voir le document" iframeTitle="Aperçu du fichier" />
                      ) : (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <FileText className="size-5" />
                          <span className="text-sm">En attente...</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-1 items-center justify-end gap-2">
                      <span className="text-sm text-muted-foreground">Type du document</span>
                      <FormControl>
                        <Select value={labelField.value} onValueChange={labelField.onChange} disabled={isThisSlotUploading}>
                          <SelectTrigger className="w-[120px]">
                            <SelectValue placeholder="Choisir" />
                          </SelectTrigger>
                          <SelectContent>
                            {ATTACHMENT_LABEL_OPTIONS.map(option => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(index)}
                      type="button"
                      className="text-red-500 hover:text-red-600"
                      disabled={isThisSlotUploading}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          );
        })}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <Button variant="outline" onClick={handleAddClick} type="button" className="w-full" disabled={isUploading}>
        <Plus className="mr-2 size-4" />
        Ajouter un document
      </Button>

      <p className="text-xs text-muted-foreground">PDF uniquement, 10 Mo maximum</p>
    </div>
  );
};
